import { useMemo } from 'react';
import { EventPageFragment, EventPageType } from '@graphql/generated';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';
import { MigratedCustomPageSlug } from '@shared/constants/customPageMigration';
import { getLegacyPageType } from '@shared/utils/migratedCustomPages';

interface UseFilterMigratedLegacyPagesReturn {
  readonly filteredPages: ReadonlyArray<EventPageFragment>;
  readonly isLoading: boolean;
}

/**
 * Hook to filter out legacy native pages when they have been migrated to custom pages.
 *
 * When a page-level "migrated custom page" event feature flag is enabled (e.g., `useStoryAsCustomPage`)
 * and a custom page exists with the migrated slug (e.g., 'story'), this hook filters out the
 * corresponding legacy page (e.g., EventPageType.story) to avoid duplicates.
 *
 * @param pages - Array of event pages to filter
 * @returns Object containing filtered pages and loading state
 *
 * @example
 * const { filteredPages, isLoading } = useFilterMigratedLegacyPages(event.pages);
 */
export const useFilterMigratedLegacyPages = (pages: ReadonlyArray<EventPageFragment>): UseFilterMigratedLegacyPagesReturn => {
  const { isLoading, isMigratedCustomPageEnabled } = useMigratedCustomPageFeatureFlags();

  const filteredPages = useMemo(() => {
    if (isLoading) {
      return pages;
    }

    // Find migrated custom pages whose per-page event flag is enabled.
    const migratedCustomPageSlugs = new Set(pages.filter(page => page.type === EventPageType.custom && isMigratedCustomPageEnabled(page.pageSlug)).map(page => page.pageSlug));

    // Determine which legacy page types should be filtered out
    const legacyTypesToFilter = new Set<EventPageType>();
    migratedCustomPageSlugs.forEach(slug => {
      const legacyType = getLegacyPageType(slug as MigratedCustomPageSlug);
      if (legacyType) {
        legacyTypesToFilter.add(legacyType);
      }
    });

    // Filter out legacy pages that have been migrated to custom pages
    return pages.filter(page => !legacyTypesToFilter.has(page.type));
  }, [pages, isMigratedCustomPageEnabled, isLoading]);

  return { filteredPages, isLoading };
};
